import { ArrowLeftRight, Bell as BellIcon, Gift, Menu, Receipt, ShieldAlert, TrendingUp, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import BrandLogo from "@/components/common/BrandLogo";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import { PROFILE_LINKS, SITE_NAV, isNavActive } from "@/lib/accountNav";

function LockGlyph() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className="opacity-60" aria-hidden>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function navTestId(label) {
  return `nav-${label.toLowerCase().replace(/\s+/g, "-")}`;
}

function displayNameOf(user) {
  return user.full_name || (user.email ? user.email.split("@")[0] : "Account");
}

function useGoHome() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return () => {
    if (pathname === "/" || pathname === "") {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      return;
    }
    navigate("/");
  };
}

function relativeTime(ts) {
  const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (sec < 60) return "Just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function kindOf(type = "") {
  if (type === "reward") return "reward";
  if (type.startsWith("tds")) return "tax";
  if (/price|market/.test(type)) return "price";
  if (/login|security|session|password|kyc/.test(type)) return "security";
  return "trade";
}

function kindMeta(kind) {
  if (kind === "trade") return { Icon: ArrowLeftRight, wrap: "bg-up/15 text-up", label: "Trade" };
  if (kind === "reward") return { Icon: Gift, wrap: "bg-up/15 text-up", label: "Reward" };
  if (kind === "tax") return { Icon: Receipt, wrap: "bg-primary/15 text-primary", label: "TDS" };
  if (kind === "price") return { Icon: TrendingUp, wrap: "bg-primary/15 text-primary", label: "Price" };
  return { Icon: ShieldAlert, wrap: "bg-warning/15 text-warning", label: "Security" };
}

const PREF_OF_KIND = { trade: "trades", reward: "trades", tax: "trades", price: "price", security: "security" };

const PUSH_CHOICES = [
  { key: "trades", label: "Trades", hint: "Fills and escrow" },
  { key: "price", label: "Price alerts", hint: "Moves on your watchlist" },
  { key: "security", label: "Security", hint: "Logins and account changes" },
  { key: "marketing", label: "Product news", hint: "Desk updates" },
];

const PUSH_DEVICE_KEY = "paisacx.push";
const PREFS_EVENT = "paisacx:notif-prefs";

export function useNotifPrefs(enabled = true) {
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const load = () =>
      api
        .get("/api/user/notification-prefs")
        .then(({ data }) => alive && setPrefs(data))
        .catch(() => {});
    load();
    window.addEventListener(PREFS_EVENT, load);
    return () => {
      alive = false;
      window.removeEventListener(PREFS_EVENT, load);
    };
  }, [enabled]);

  const save = useCallback(async (key, value) => {
    setPrefs((p) => ({ ...(p || {}), [key]: value }));
    try {
      const { data } = await api.patch("/api/user/notification-prefs", { [key]: value });
      setPrefs(data);
      window.dispatchEvent(new Event(PREFS_EVENT));
    } catch {
      setPrefs((p) => ({ ...(p || {}), [key]: !value }));
    }
  }, []);

  return { prefs, save };
}

function PushSwitch({ on, label, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onChange}
      className={`h-5 w-9 shrink-0 rounded-full p-0.5 transition ${on ? "brand-gradient" : "border border-border bg-surface-2"}`}
    >
      <span className={`block h-4 w-4 rounded-full bg-foreground transition-transform ${on ? "translate-x-4" : ""}`} />
    </button>
  );
}

function PushOptions() {
  const { prefs, save } = useNotifPrefs();
  const [deviceOn, setDeviceOn] = useState(false);
  const [permission, setPermission] = useState("default");

  useEffect(() => {
    if (typeof Notification === "undefined") {
      setPermission("unsupported");
      return;
    }
    setPermission(Notification.permission);
    setDeviceOn(Notification.permission === "granted" && localStorage.getItem(PUSH_DEVICE_KEY) !== "off");
  }, []);

  async function toggleDevice() {
    if (typeof Notification === "undefined") return;
    if (deviceOn) {
      setDeviceOn(false);
      localStorage.setItem(PUSH_DEVICE_KEY, "off");
      return;
    }
    const result = await Notification.requestPermission();
    setPermission(result);
    const granted = result === "granted";
    setDeviceOn(granted);
    localStorage.setItem(PUSH_DEVICE_KEY, granted ? "on" : "off");
  }

  const deviceHint =
    permission === "unsupported"
      ? "This browser cannot show alerts"
      : permission === "denied"
        ? "Blocked in the browser settings"
        : deviceOn
          ? "Alerts can reach this browser"
          : "Off for this browser";

  return (
    <div className="shrink-0 border-t border-border px-3 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Push notifications</p>
      <ul className="mt-2 space-y-1">
        <li className="flex items-center justify-between gap-3 rounded-lg px-1 py-1.5">
          <span className="min-w-0">
            <span className="block text-sm">This device</span>
            <span className="block text-[11px] text-muted-foreground">{deviceHint}</span>
          </span>
          <PushSwitch on={deviceOn} label="This device" onChange={() => void toggleDevice()} />
        </li>
        {PUSH_CHOICES.map((choice) => (
          <li key={choice.key} className="flex items-center justify-between gap-3 rounded-lg px-1 py-1.5">
            <span className="min-w-0">
              <span className="block text-sm">{choice.label}</span>
              <span className="block text-[11px] text-muted-foreground">{choice.hint}</span>
            </span>
            <PushSwitch
              on={Boolean(prefs?.[choice.key])}
              label={choice.label}
              onChange={() => prefs && void save(choice.key, !prefs[choice.key])}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function Bell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const { prefs } = useNotifPrefs();
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const load = useCallback(() => {
    api
      .get("/api/user/notifications", { params: { limit: 30 } })
      .then(({ data }) => setItems(data?.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const markAllRead = useCallback(() => {
    setItems((list) => {
      if (!list.some((n) => !n.read)) return list;
      api.post("/api/user/notifications/mark-read", { notification_ids: [] }).catch(() => {});
      return list.map((n) => ({ ...n, read: true }));
    });
  }, []);

  const visible = items
    .map((n) => ({ ...n, kind: kindOf(n.type) }))
    .filter((n) => !prefs || prefs[PREF_OF_KIND[n.kind]] !== false);
  const unread = visible.filter((n) => !n.read).length;
  const shown = visible.slice(0, 8);

  useEffect(() => {
    if (!open) return undefined;
    function dismiss() {
      setOpen(false);
      markAllRead();
    }
    function onPointer(e) {
      if (buttonRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      dismiss();
    }
    function onKey(e) {
      if (e.key === "Escape") dismiss();
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, markAllRead]);

  const panel = open
    ? createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Notifications"
          data-testid="notifications-panel"
          className="page-fade fixed left-3 right-3 top-[4.25rem] z-[80] flex max-h-[min(78vh,36rem)] flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-panel sm:left-auto sm:right-4 sm:w-[22rem]"
        >
          <div className="flex items-center justify-between gap-3 border-b border-border bg-[linear-gradient(120deg,oklch(var(--primary)/0.14),transparent)] px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold">Notifications</p>
              <p className="truncate text-[11px] text-muted-foreground">
                {unread > 0 ? `${unread} unread update${unread === 1 ? "" : "s"}` : "You're all caught up"}
              </p>
            </div>
            {items.length > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="shrink-0 rounded-md px-2 py-1 text-[11px] font-medium text-primary transition hover:bg-primary/10"
              >
                Mark all read
              </button>
            )}
          </div>

          {shown.length === 0 ? (
            <div className="flex flex-col items-center px-4 py-10 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                <BellIcon className="h-5 w-5" />
              </span>
              <p className="mt-3 text-sm font-medium">No notifications yet</p>
              <p className="mt-1 max-w-[16rem] text-xs leading-5 text-muted-foreground">
                Trade fills, price alerts and security notices will show up here.
              </p>
            </div>
          ) : (
            <ul className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5">
              {shown.map((n) => {
                const { Icon, wrap, label } = kindMeta(n.kind);
                return (
                  <li key={n.notification_id}>
                    <div
                      className={`flex gap-3 rounded-xl px-3 py-2.5 transition hover:bg-surface-2 ${
                        n.read ? "opacity-80" : "bg-primary/[0.06]"
                      }`}
                    >
                      <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${wrap}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className="truncate text-sm font-semibold leading-5">{n.title}</p>
                          {!n.read && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                        </div>
                        <p className="mt-0.5 text-xs leading-5 text-muted-foreground">{n.message}</p>
                        <div className="mt-1.5 flex items-center gap-2 text-[10px] text-muted-foreground">
                          <span className="rounded border border-border px-1.5 py-0.5 font-medium uppercase tracking-wide">
                            {label}
                          </span>
                          <span className="num">{n.created_at ? relativeTime(new Date(n.created_at).getTime()) : ""}</span>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <PushOptions />
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Notifications"
        aria-expanded={open}
        aria-haspopup="dialog"
        data-testid="nav-bell"
        onClick={() =>
          setOpen((o) => {
            if (o) markAllRead();
            return !o;
          })
        }
        className={`relative flex h-9 w-9 items-center justify-center rounded-lg border bg-surface-2 text-foreground transition duration-200 hover:border-primary hover:bg-primary/10 hover:text-primary ${
          open ? "border-primary bg-primary/10 text-primary" : "border-border"
        }`}
      >
        <BellIcon className="h-4 w-4" />
        {unread > 0 && (
          <span className="num absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-down px-1 text-[10px] font-semibold text-white shadow-sm ring-2 ring-background">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {panel}
    </>
  );
}

function MobileDrawer({ open, onClose, user, pathname }) {
  const { logout } = useAuth();
  const nav = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const goHome = useGoHome();

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] md:hidden" data-testid="nav-mobile-drawer">
      <button
        type="button"
        aria-label="Close menu"
        data-testid="nav-mobile-backdrop"
        className="absolute inset-0 bg-background/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="page-fade absolute inset-y-0 right-0 flex w-[min(20rem,88vw)] flex-col border-l border-border bg-surface shadow-panel">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <p className="text-sm font-semibold">Menu</p>
          <button
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface-2 transition hover:border-primary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-3">
          <ul className="space-y-1">
            {SITE_NAV.map((item) => {
              const active = isNavActive(pathname, item.to);
              const locked = item.protected && !user;
              const cls = `flex w-full items-center justify-between rounded-xl px-3 py-3 text-sm font-medium transition ${
                active ? "bg-primary/[0.12] text-foreground" : "text-muted-foreground hover:bg-surface-2 hover:text-foreground"
              }`;
              if (item.to === "/") {
                return (
                  <li key={item.to}>
                    <button
                      type="button"
                      className={cls}
                      data-testid={`mobile-${navTestId(item.label)}`}
                      onClick={() => {
                        goHome();
                        onClose();
                      }}
                    >
                      {item.label}
                    </button>
                  </li>
                );
              }
              return (
                <li key={item.to}>
                  <Link
                    to={locked ? "/login" : item.to}
                    state={locked ? { from: item.to } : undefined}
                    onClick={onClose}
                    data-testid={`mobile-${navTestId(item.label)}`}
                    className={cls}
                  >
                    <span>{item.label}</span>
                    {locked ? <LockGlyph /> : null}
                  </Link>
                </li>
              );
            })}
          </ul>

          {user && <PushOptions />}
          {user && (
            <div className="mt-4 border-t border-border pt-4">
              <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Account</p>
              <ul className="space-y-1">
                {PROFILE_LINKS.map(({ to, label }) => (
                  <li key={to}>
                    <Link
                      to={to}
                      onClick={onClose}
                      data-testid={`mobile-profile-${to.split("/").pop()}`}
                      className="block rounded-xl px-3 py-2.5 text-sm text-muted-foreground transition hover:bg-surface-2 hover:text-foreground"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
                {user.role === "admin" && (
                  <li>
                    <Link
                      to="/admin/dashboard"
                      onClick={onClose}
                      data-testid="mobile-nav-admin"
                      className="block rounded-xl px-3 py-2.5 text-sm text-primary transition hover:bg-surface-2"
                    >
                      Admin console
                    </Link>
                  </li>
                )}
                <li>
                  <button
                    type="button"
                    onClick={async () => {
                      await logout();
                      onClose();
                      nav("/");
                    }}
                    className="w-full rounded-xl px-3 py-2.5 text-left text-sm text-down transition hover:bg-down/10"
                  >
                    Log out
                  </button>
                </li>
              </ul>
            </div>
          )}
        </nav>

        <div className="border-t border-border p-3 pb-safe">
          <button
            type="button"
            onClick={toggleTheme}
            className="flex w-full items-center justify-between rounded-xl border border-border bg-surface-2 px-3 py-2.5 text-sm transition hover:border-primary"
          >
            <span>Theme</span>
            <span className="text-muted-foreground">{theme === "dark" ? "Dark ☾" : "Light ☀"}</span>
          </button>
          {!user && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Link
                to="/login"
                onClick={onClose}
                className="grid place-items-center rounded-xl border border-border bg-surface-2 px-3 py-2.5 text-sm transition hover:border-primary"
              >
                Log in
              </Link>
              <Link
                to="/register"
                onClick={onClose}
                className="brand-gradient grid place-items-center rounded-xl px-3 py-2.5 text-sm font-semibold text-primary-foreground"
              >
                Sign up
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProfileMenu({ user }) {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const displayName = displayNameOf(user);

  return (
    <div className="relative hidden sm:block" ref={wrapRef}>
      <button
        type="button"
        aria-expanded={open}
        data-testid="nav-profile"
        onClick={() => setOpen((m) => !m)}
        className="flex max-w-[140px] items-center gap-2 rounded-lg border border-border bg-surface-2 py-1 pl-1 pr-2 transition hover:border-primary sm:max-w-none sm:pr-3"
      >
        <span className="brand-gradient flex h-7 w-7 items-center justify-center rounded-md text-xs font-semibold text-primary-foreground">
          {displayName.slice(0, 1).toUpperCase()}
        </span>
        <span className="hidden text-sm sm:block">{displayName.split(" ")[0]}</span>
      </button>
      {open && (
        <div
          className="page-fade absolute right-0 z-[60] mt-2 w-52 rounded-xl border border-border bg-surface p-1.5 shadow-panel"
          data-testid="profile-menu"
        >
          {PROFILE_LINKS.map((i) => (
            <Link
              key={i.to}
              to={i.to}
              onClick={() => setOpen(false)}
              data-testid={`profile-menu-${i.to.split("/").pop()}`}
              className="block rounded-lg px-3 py-2 text-sm text-muted-foreground transition hover:bg-surface-2 hover:text-foreground"
            >
              {i.label}
            </Link>
          ))}
          {user.role === "admin" && (
            <Link
              to="/admin/dashboard"
              onClick={() => setOpen(false)}
              data-testid="profile-menu-admin"
              className="block rounded-lg px-3 py-2 text-sm text-primary transition hover:bg-surface-2"
            >
              Admin console
            </Link>
          )}
          <button
            type="button"
            data-testid="profile-menu-logout"
            onClick={async () => {
              setOpen(false);
              await logout();
              nav("/");
            }}
            className="mt-1 block w-full rounded-lg px-3 py-2 text-left text-sm text-down transition hover:bg-down/10"
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { theme, toggleTheme } = useTheme();
  const goHome = useGoHome();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const loggedIn = Boolean(user);

  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  return (
    <>
      <header data-testid="site-header" className="fixed inset-x-0 top-0 z-40 w-full min-w-0 max-w-full pt-safe">
        <div className="border-b border-border bg-page/85 backdrop-blur-md">
          <div className="mx-auto flex h-14 w-full max-w-7xl min-w-0 items-center justify-between gap-2 px-3 sm:h-16 sm:px-4">
            <button type="button" aria-label="PaisaCX home" data-testid="nav-logo" onClick={goHome} className="shrink-0 cursor-pointer">
              <span className="sm:hidden">
                <BrandLogo size="sm" wordmark={false} testId="nav-logo-img" />
              </span>
              <span className="hidden sm:inline-flex">
                <BrandLogo size="sm" />
              </span>
            </button>

            <nav className="hidden min-w-0 flex-1 items-center justify-center gap-0.5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] md:flex lg:gap-1 [&::-webkit-scrollbar]:hidden">
              {SITE_NAV.map((item) => {
                const active = isNavActive(pathname, item.to);
                const locked = item.protected && !loggedIn;
                const cls = `flex shrink-0 items-center gap-1 rounded-lg px-2 py-1.5 text-sm transition lg:gap-1.5 lg:px-3 ${
                  active ? "bg-surface-2 text-foreground" : "text-muted-foreground hover:text-foreground"
                }`;
                if (item.to === "/") {
                  return (
                    <button key={item.to} type="button" className={cls} onClick={goHome} data-testid={navTestId(item.label)}>
                      {item.label}
                    </button>
                  );
                }
                return (
                  <Link
                    key={item.to}
                    to={locked ? "/login" : item.to}
                    state={locked ? { from: item.to } : undefined}
                    title={locked ? `Log in to access ${item.label} trading` : undefined}
                    data-testid={navTestId(item.label)}
                    className={cls}
                  >
                    {item.label}
                    {locked ? <LockGlyph /> : null}
                  </Link>
                );
              })}
            </nav>

            <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
              <button
                type="button"
                aria-label="Toggle theme"
                data-testid="theme-toggle"
                onClick={toggleTheme}
                className="hidden h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface-2 text-muted-foreground transition hover:text-foreground sm:flex"
              >
                {theme === "dark" ? "☾" : "☀"}
              </button>
              {loggedIn ? (
                <>
                  <Bell />
                  <ProfileMenu user={user} />
                </>
              ) : (
                <div className="hidden shrink-0 items-center gap-1.5 sm:flex">
                  <Link
                    to="/login"
                    data-testid="nav-login"
                    className="rounded-lg border border-border bg-surface-2 px-3.5 py-2 text-sm transition hover:border-primary"
                  >
                    Log in
                  </Link>
                  <Link
                    to="/register"
                    data-testid="nav-register"
                    className="brand-gradient rounded-lg px-3.5 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
                  >
                    Sign up
                  </Link>
                </div>
              )}

              <button
                type="button"
                aria-label="Open menu"
                aria-expanded={drawerOpen}
                data-testid="nav-mobile-toggle"
                onClick={() => setDrawerOpen(true)}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface-2 text-foreground transition hover:border-primary md:hidden"
              >
                <Menu className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </header>
      <div className="h-14 shrink-0 sm:h-16" aria-hidden />
      <AnnouncementBanner />
      {typeof document !== "undefined" &&
        createPortal(
          <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} user={loggedIn ? user : null} pathname={pathname} />,
          document.body,
        )}
    </>
  );
}
